import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  LifeBuoy, Plus, Bug, Lightbulb, Sparkles, HelpCircle,
  X, AlertTriangle, RefreshCw, Bot, Wand2, Paperclip,
  Clock, Shield, Rocket, Wrench, CheckCircle, Target,
} from 'lucide-react';
import { colors, API, authHeaders } from '../utils/theme';

// ─────────────────────────────────────
// Status config
// ─────────────────────────────────────
const STATUS_LABEL = {
  ABERTO: 'Aberto',
  APROVADO_GESTOR: 'Aprovado (gestor)',
  LIBERADO_DEV: 'Liberado p/ Dev',
  EM_DESENVOLVIMENTO: 'Em desenvolvimento',
  AGUARDANDO_FEEDBACK: 'Aguardando feedback',
  ENTREGUE: 'Entregue',
  REJEITADO_GESTOR: 'Rejeitado (gestor)',
  REJEITADO_ADMIN: 'Rejeitado (admin)',
  REJEITADO_USUARIO: 'Rejeitado (usuário)',
};

const STATUS_COLOR = {
  ABERTO: colors.textMuted,
  APROVADO_GESTOR: colors.info,
  LIBERADO_DEV: '#8b5cf6',
  EM_DESENVOLVIMENTO: colors.warning,
  AGUARDANDO_FEEDBACK: '#f97316',
  ENTREGUE: colors.success,
  REJEITADO_GESTOR: colors.danger,
  REJEITADO_ADMIN: colors.danger,
  REJEITADO_USUARIO: colors.danger,
};

const CATEGORIA_LABEL = {
  bug: 'Bug',
  melhoria: 'Melhoria',
  nova_feature: 'Nova Feature',
  duvida: 'Dúvida',
};

const CATEGORIA_EMOJI = {
  bug: '🐛',
  melhoria: '💡',
  nova_feature: '✨',
  duvida: '❓',
};

const CATEGORIA_COLOR = {
  bug: colors.danger,
  melhoria: colors.info,
  nova_feature: '#8b5cf6',
  duvida: colors.warning,
};

const PRIORIDADE_LABEL = {
  baixa: 'Baixa',
  media: 'Média',
  alta: 'Alta',
  critica: 'Crítica',
};

const PRIORIDADE_COLOR = {
  baixa: colors.textDim,
  media: colors.info,
  alta: colors.warning,
  critica: colors.danger,
};

function CategoriaIcon({ categoria, size = 14 }) {
  const props = { size };
  if (categoria === 'bug') return <Bug {...props} />;
  if (categoria === 'melhoria') return <Lightbulb {...props} />;
  if (categoria === 'nova_feature') return <Sparkles {...props} />;
  return <HelpCircle {...props} />;
}

function StatusBadge({ status }) {
  const color = STATUS_COLOR[status] || colors.textMuted;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
      background: `${color}22`, color, border: `1px solid ${color}44`,
      whiteSpace: 'nowrap',
    }}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function PrioridadeBadge({ prioridade }) {
  const color = PRIORIDADE_COLOR[prioridade] || colors.textMuted;
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700,
      background: `${color}22`, color, border: `1px solid ${color}44`,
      whiteSpace: 'nowrap',
    }}>
      {PRIORIDADE_LABEL[prioridade] || prioridade}
    </span>
  );
}

// ─────────────────────────────────────
// Tabs config
// ─────────────────────────────────────
const TABS = [
  { id: 'meus', label: 'Meus chamados', Icon: Clock },
  { id: 'em-aberto', label: 'Em aberto', Icon: Clock },
  { id: 'aguardando-feedback', label: 'Aguardando meu feedback', Icon: Shield },
  { id: 'a-validar', label: 'A validar', Icon: Shield, adminOnly: true },
  { id: 'a-liberar', label: 'A liberar', Icon: Rocket, adminOnly: true },
  { id: 'fila-dev', label: 'Fila Dev', Icon: Wrench, adminOnly: true },
  { id: 'em-homologacao', label: 'Em homologação', Icon: Shield, adminOnly: true },
  { id: 'entregues', label: 'Entregues', Icon: CheckCircle },
  { id: 'todos', label: 'Todos', Icon: Target, adminOnly: true },
];

// Retorna filtro para aplicar no endpoint/no front para cada tab
function filtroPorTab(tab, userId) {
  switch (tab) {
    case 'meus': return { mine: '1' };
    case 'em-aberto': return { mine: '1', __multiStatus: ['ABERTO','APROVADO_GESTOR','LIBERADO_DEV','EM_DESENVOLVIMENTO'] };
    case 'aguardando-feedback': return { mine: '1', status: 'AGUARDANDO_FEEDBACK' };
    case 'a-validar': return { status: 'ABERTO' };
    case 'a-liberar': return { status: 'APROVADO_GESTOR' };
    case 'fila-dev': return { __multiStatus: ['LIBERADO_DEV', 'EM_DESENVOLVIMENTO'] };
    case 'em-homologacao': return { status: 'AGUARDANDO_FEEDBACK' };
    case 'entregues': return { status: 'ENTREGUE' };
    case 'todos': return {};
    default: return {};
  }
}

// ─────────────────────────────────────
// ChamadosPage
// ─────────────────────────────────────
export default function ChamadosPage({ onNavigate }) {
  const [tab, setTab] = useState('meus');
  const [chamados, setChamados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalNovo, setModalNovo] = useState(false);
  const [modalOrientacoes, setModalOrientacoes] = useState(false);
  const [iaInfo, setIaInfo] = useState(null);
  const [counts, setCounts] = useState({}); // contadores por tab
  const [modoSelecao, setModoSelecao] = useState(false);
  const [selecionados, setSelecionados] = useState(new Set());
  const [bulkStatus, setBulkStatus] = useState('ENTREGUE');
  const [bulkLoading, setBulkLoading] = useState(false);

  const usuario = JSON.parse(localStorage.getItem('xm_usuario') || '{}');
  const isAdmin = (usuario.perfis || []).includes('ADMIN');

  const tabsVisiveis = TABS.filter(t => !t.adminOnly || isAdmin);

  const fetchChamados = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filtro = filtroPorTab(tab, usuario.id);
      const params = new URLSearchParams();
      if (filtro.mine) params.set('mine', filtro.mine);
      if (filtro.status) params.set('status', filtro.status);
      const qs = params.toString();
      const url = `${API}/chamados${qs ? '?' + qs : ''}`;
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      let lista = data.data || [];
      if (filtro.__multiStatus) {
        lista = lista.filter(c => filtro.__multiStatus.includes(c.status));
      }
      setChamados(lista);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [tab, usuario.id]);

  // Busca contadores das tabs em paralelo (apenas os que fazem sentido mostrar badge)
  const fetchCounts = useCallback(async () => {
    try {
      const endpoints = [
        { id: 'aguardando-feedback', url: `${API}/chamados?mine=1&status=AGUARDANDO_FEEDBACK` },
      ];
      if (isAdmin) {
        endpoints.push(
          { id: 'a-validar', url: `${API}/chamados?status=ABERTO` },
          { id: 'a-liberar', url: `${API}/chamados?status=APROVADO_GESTOR` },
          { id: 'em-homologacao', url: `${API}/chamados?status=AGUARDANDO_FEEDBACK` },
        );
      }
      const results = await Promise.all(endpoints.map(e =>
        fetch(e.url, { headers: authHeaders() }).then(r => r.ok ? r.json() : { data: [] })
          .then(d => ({ id: e.id, n: (d.data || []).length }))
          .catch(() => ({ id: e.id, n: 0 }))
      ));
      // Fila Dev: buscar todos e filtrar
      if (isAdmin) {
        try {
          const r = await fetch(`${API}/chamados`, { headers: authHeaders() });
          const d = r.ok ? await r.json() : { data: [] };
          const n = (d.data || []).filter(c => c.status === 'LIBERADO_DEV' || c.status === 'EM_DESENVOLVIMENTO').length;
          results.push({ id: 'fila-dev', n });
        } catch {}
      }
      const m = {};
      results.forEach(r => { m[r.id] = r.n; });
      setCounts(m);
    } catch {}
  }, [isAdmin]);

  useEffect(() => { fetchChamados(); }, [fetchChamados]);
  useEffect(() => { fetchCounts(); }, [fetchCounts, tab]);

  const handleCriado = (chamado) => {
    setModalNovo(false);
    setIaInfo({ chamadoId: chamado.id, titulo: chamado.titulo });
    fetchChamados();
    fetchCounts();
    setTimeout(() => setIaInfo(null), 8000);
  };

  const abrirDetalhe = (id) => {
    localStorage.setItem('chamado_id_atual', String(id));
    onNavigate('chamado-detalhe');
  };

  const toggleSelecao = (id) => setSelecionados(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const toggleTodos = () => {
    if (selecionados.size === chamados.length) setSelecionados(new Set());
    else setSelecionados(new Set(chamados.map(c => c.id)));
  };
  const sairSelecao = () => { setModoSelecao(false); setSelecionados(new Set()); };

  // Codar todos da Fila Dev: move os LIBERADO_DEV (ainda não iniciados) para
  // EM_DESENVOLVIMENTO de uma vez. Os que já estão EM_DESENVOLVIMENTO ficam.
  const codarTodosFilaDev = async () => {
    const aIniciar = chamados.filter(c => c.status === 'LIBERADO_DEV').map(c => c.id);
    if (!aIniciar.length) { alert('Nenhum chamado em "Liberado p/ Dev" pra iniciar.'); return; }
    if (!window.confirm(`Iniciar desenvolvimento de ${aIniciar.length} chamado(s) da fila?\nTodos vão para "Em desenvolvimento".`)) return;
    setBulkLoading(true);
    try {
      const res = await fetch(`${API}/chamados/bulk-status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ chamado_ids: aIniciar, status: 'EM_DESENVOLVIMENTO' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      alert(`▶ ${data.atualizados} chamado(s) entraram em desenvolvimento.`);
      fetchChamados();
      fetchCounts();
    } catch (e) { alert('Erro: ' + e.message); }
    setBulkLoading(false);
  };

  const aplicarBulkStatus = async () => {
    if (!selecionados.size) return;
    setBulkLoading(true);
    try {
      const res = await fetch(`${API}/chamados/bulk-status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ chamado_ids: [...selecionados], status: bulkStatus }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      alert(`✓ ${data.atualizados} chamado(s) → ${STATUS_LABEL[bulkStatus] || bulkStatus}`);
      sairSelecao();
      fetchChamados();
      fetchCounts();
    } catch (e) { alert('Erro: ' + e.message); }
    setBulkLoading(false);
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700, color: colors.text, letterSpacing: -0.5 }}>Chamados</h1>
          <p style={{ margin: '4px 0 0 0', fontSize: 14, color: colors.textMuted }}>
            Solicitações de ajuste e correção do sistema
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {isAdmin && (
            <button
              onClick={() => modoSelecao ? sairSelecao() : setModoSelecao(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 14px', background: 'transparent',
                border: `1px solid ${modoSelecao ? colors.warning : colors.border}`,
                borderRadius: 8, color: modoSelecao ? colors.warning : colors.text,
                fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}
            >
              {modoSelecao ? '✕ Cancelar' : '☑ Selecionar'}
            </button>
          )}
          <button
            onClick={() => setModalOrientacoes(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 14px', background: 'transparent', border: `1px solid ${colors.border}`,
              borderRadius: 8, color: colors.text, fontSize: 13, fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            <HelpCircle size={14} /> Orientações
          </button>
          <button
            onClick={() => setModalNovo(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 16px', background: colors.primary, border: 'none',
              borderRadius: 8, color: colors.white, fontSize: 13, fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            <Plus size={14} /> Novo chamado
          </button>
        </div>
      </div>

      {/* Aviso IA */}
      {iaInfo && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 16px', background: `${colors.info}15`,
          border: `1px solid ${colors.info}40`, borderRadius: 8,
          marginBottom: 16, fontSize: 13, color: colors.info,
        }}>
          <Bot size={16} />
          <span>
            Chamado <strong>"{iaInfo.titulo}"</strong> criado!
            A IA está analisando em background.
          </span>
          <button onClick={() => setIaInfo(null)} style={{ background: 'none', border: 'none', color: colors.info, cursor: 'pointer', marginLeft: 'auto' }}>
            <X size={14} />
          </button>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, borderBottom: `1px solid ${colors.border}`, marginBottom: 20, overflowX: 'auto' }}>
        {tabsVisiveis.map(t => {
          const ativo = tab === t.id;
          const count = counts[t.id] || 0;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
                padding: '10px 14px', border: 'none', cursor: 'pointer', fontSize: 13,
                fontWeight: ativo ? 600 : 500,
                color: ativo ? colors.primary : colors.textMuted,
                background: 'transparent',
                borderBottom: ativo ? `2px solid ${colors.primary}` : '2px solid transparent',
                marginBottom: -1,
                transition: 'all 0.15s',
              }}
            >
              <t.Icon size={14} />
              {t.label}
              {count > 0 && (
                <span style={{
                  background: ativo ? colors.primary : colors.textDim,
                  color: colors.white, fontSize: 10, fontWeight: 700,
                  padding: '1px 7px', borderRadius: 10, minWidth: 18, textAlign: 'center',
                }}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
        <button
          onClick={() => { fetchChamados(); fetchCounts(); }}
          style={{
            marginLeft: 'auto', background: 'none', border: 'none',
            color: colors.textDim, cursor: 'pointer', padding: '8px',
          }}
          title="Recarregar"
        >
          <RefreshCw size={14} />
        </button>
      </div>

      {/* Content */}
      {loading && (
        <div style={{ textAlign: 'center', padding: 60, color: colors.textMuted }}>
          <LifeBuoy size={28} style={{ opacity: 0.5, marginBottom: 8 }} />
          <div>Carregando chamados...</div>
        </div>
      )}

      {!loading && error && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '12px 16px', background: `${colors.danger}15`,
          border: `1px solid ${colors.danger}40`, borderRadius: 8, color: colors.danger,
        }}>
          <AlertTriangle size={16} />
          Erro ao carregar chamados: {error}
        </div>
      )}

      {!loading && !error && chamados.length === 0 && (
        <div style={{ textAlign: 'center', padding: 60, color: colors.textMuted }}>
          <LifeBuoy size={36} style={{ opacity: 0.3, marginBottom: 12 }} />
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>
            Nenhum chamado nesta aba
          </div>
          <div style={{ fontSize: 13 }}>
            Use "Novo chamado" para abrir uma solicitação.
          </div>
        </div>
      )}

      {!loading && !error && chamados.length > 0 && (
        <>
          {/* Ação da aba Fila Dev: codar todos os liberados de uma vez (ADMIN) */}
          {isAdmin && tab === 'fila-dev' && !modoSelecao && (() => {
            const nLiberados = chamados.filter(c => c.status === 'LIBERADO_DEV').length;
            return (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                <button
                  onClick={codarTodosFilaDev}
                  disabled={bulkLoading || nLiberados === 0}
                  title={nLiberados === 0 ? 'Nenhum chamado liberado pra iniciar' : 'Move todos os liberados para Em desenvolvimento'}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '8px 16px', borderRadius: 8, border: 'none',
                    background: nLiberados > 0 ? colors.warning : colors.border,
                    color: nLiberados > 0 ? '#000' : colors.textDim,
                    fontSize: 13, fontWeight: 700,
                    cursor: nLiberados > 0 && !bulkLoading ? 'pointer' : 'not-allowed',
                  }}
                >
                  <Wrench size={14} /> {bulkLoading ? 'Iniciando...' : `Codar todos (${nLiberados})`}
                </button>
              </div>
            );
          })()}
          {modoSelecao && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, fontSize: 13, color: colors.textMuted }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="checkbox" checked={selecionados.size === chamados.length && chamados.length > 0}
                  onChange={toggleTodos} style={{ cursor: 'pointer' }} />
                Selecionar todos ({chamados.length})
              </label>
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {chamados.map(c => (
              <ChamadoCard
                key={c.id}
                chamado={c}
                onClick={() => modoSelecao ? toggleSelecao(c.id) : abrirDetalhe(c.id)}
                selecionavel={modoSelecao}
                selecionado={selecionados.has(c.id)}
                onToggle={() => toggleSelecao(c.id)}
              />
            ))}
          </div>
        </>
      )}

      {/* Barra flutuante de bulk */}
      {modoSelecao && selecionados.size > 0 && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          background: colors.card, border: `1px solid ${colors.border}`,
          borderRadius: 12, padding: '12px 20px', display: 'flex', alignItems: 'center',
          gap: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.4)', zIndex: 1000, flexWrap: 'wrap',
        }}>
          <span style={{ color: colors.text, fontWeight: 600 }}>
            ✓ {selecionados.size} selecionado{selecionados.size !== 1 ? 's' : ''}
          </span>
          <div style={{ width: 1, height: 24, background: colors.border }} />
          <span style={{ color: colors.textMuted, fontSize: 13 }}>Mudar status para:</span>
          <select value={bulkStatus} onChange={e => setBulkStatus(e.target.value)}
            style={{ background: colors.input, color: colors.text, border: `1px solid ${colors.border}`, borderRadius: 6, padding: '5px 10px', fontSize: 13 }}>
            {Object.entries(STATUS_LABEL).map(([v, label]) => (
              <option key={v} value={v}>{label}</option>
            ))}
          </select>
          <button onClick={aplicarBulkStatus} disabled={bulkLoading}
            style={{ padding: '6px 16px', borderRadius: 6, border: 'none', cursor: 'pointer', background: colors.primary, color: '#fff', fontSize: 13, fontWeight: 700 }}>
            {bulkLoading ? 'Aplicando...' : 'Aplicar'}
          </button>
          <button onClick={sairSelecao}
            style={{ padding: '6px 12px', borderRadius: 6, border: `1px solid ${colors.border}`, background: 'transparent', color: colors.textMuted, fontSize: 13, cursor: 'pointer' }}>
            Cancelar
          </button>
        </div>
      )}

      {modalNovo && (
        <ModalNovoChamado
          onClose={() => setModalNovo(false)}
          onCriado={handleCriado}
        />
      )}

      {modalOrientacoes && (
        <ModalOrientacoes onClose={() => setModalOrientacoes(false)} />
      )}
    </div>
  );
}

// ─────────────────────────────────────
// Card item da lista (novo layout)
// ─────────────────────────────────────
function ChamadoCard({ chamado, onClick, selecionavel, selecionado, onToggle }) {
  const data = new Date(chamado.created_at).toLocaleDateString('pt-BR');
  const catColor = CATEGORIA_COLOR[chamado.categoria] || colors.textMuted;
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 14,
        padding: '14px 16px',
        background: selecionado ? `${colors.primary}18` : colors.card,
        border: `1px solid ${selecionado ? colors.primary : colors.border}`, borderRadius: 12,
        cursor: 'pointer', transition: 'all 0.15s',
      }}
      onMouseEnter={e => {
        if (selecionado) return;
        e.currentTarget.style.borderColor = colors.primary + '80';
        e.currentTarget.style.background = colors.cardHover;
      }}
      onMouseLeave={e => {
        if (selecionado) return;
        e.currentTarget.style.borderColor = colors.border;
        e.currentTarget.style.background = colors.card;
      }}
    >
      {selecionavel && (
        <input type="checkbox" checked={!!selecionado}
          onClick={e => e.stopPropagation()}
          onChange={onToggle}
          style={{ cursor: 'pointer', marginTop: 12, flexShrink: 0 }} />
      )}
      {/* Ícone da categoria */}
      <div style={{
        width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
        background: `${catColor}22`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: catColor, fontSize: 18,
      }}>
        <CategoriaIcon categoria={chamado.categoria} size={18} />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
          <span style={{ fontWeight: 700, fontSize: 15, color: colors.text }}>
            {chamado.titulo}
          </span>
          <StatusBadge status={chamado.status} />
          <PrioridadeBadge prioridade={chamado.prioridade} />
        </div>
        <div style={{
          fontSize: 13, color: colors.textMuted, lineHeight: 1.5, marginBottom: 8,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}>
          {chamado.descricao}
        </div>
        <div style={{ fontSize: 11, color: colors.textDim }}>
          {CATEGORIA_EMOJI[chamado.categoria]} {CATEGORIA_LABEL[chamado.categoria]} · por {chamado.criador_nome} · {data}
          {chamado.ia_analise && (
            <>  · <span style={{ color: colors.info }}><Bot size={10} style={{ display: 'inline', verticalAlign: 'text-bottom' }} /> Análise IA</span></>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────
// Modal Orientações — Fluxo do sistema
// ─────────────────────────────────────
function ModalOrientacoes({ onClose }) {
  const diagrama = `FLUXO DO SISTEMA DE CHAMADOS

1. SOLICITANTE abre chamado (título + descrição + categoria + prioridade)
   → [ABERTO]  🤖 IA analisa automaticamente em background
2. GESTOR avalia → aprova ou rejeita (motivo obrigatório)
   → [APROVADO_GESTOR] ou [REJEITADO_GESTOR]
3. ADMIN libera para dev ou rejeita
   → [LIBERADO_DEV] ou [REJEITADO_ADMIN]
4. DEV inicia trabalho → [EM_DESENVOLVIMENTO]
5. DEV marca pronto → [AGUARDANDO_FEEDBACK]
6. SOLICITANTE homologa → [ENTREGUE] ✅ ou [REJEITADO_USUARIO]
7. SOLICITANTE pode reabrir com melhoria → volta para LIBERADO_DEV

💬 Em todas as etapas é possível comentar. 🤖 Botão "Reanalisar com IA" sempre disponível.
✏️  Criador edita enquanto ABERTO. ADMIN edita em qualquer status.`;

  const copiar = () => {
    navigator.clipboard.writeText(diagrama).then(() => {
      alert('Diagrama copiado!');
    });
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 16,
    }} onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: colors.card, borderRadius: 12, padding: 0,
          maxWidth: 700, width: '100%', maxHeight: '90vh',
          display: 'flex', flexDirection: 'column',
          border: `1px solid ${colors.border}`,
        }}>
        <div style={{
          padding: '16px 20px', borderBottom: `1px solid ${colors.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <HelpCircle size={20} color={colors.primary} />
            <div>
              <div style={{ fontWeight: 700, fontSize: 16, color: colors.text }}>Orientações — Fluxo de Chamados</div>
              <div style={{ fontSize: 12, color: colors.textMuted }}>Como o processo funciona</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: colors.textMuted, cursor: 'pointer', fontSize: 20 }}>×</button>
        </div>

        <div style={{ padding: 20, overflow: 'auto', flex: 1 }}>
          <pre style={{
            background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 8,
            padding: 16, fontSize: 12, lineHeight: 1.6, color: colors.text,
            fontFamily: 'monospace', whiteSpace: 'pre-wrap', overflow: 'auto', margin: 0,
          }}>{diagrama}</pre>
        </div>

        <div style={{
          padding: '12px 20px', borderTop: `1px solid ${colors.border}`,
          display: 'flex', gap: 8, justifyContent: 'flex-end',
        }}>
          <button onClick={onClose} style={{
            padding: '8px 16px', background: 'transparent', border: `1px solid ${colors.border}`,
            borderRadius: 6, color: colors.text, fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}>Fechar</button>
          <button onClick={copiar} style={{
            padding: '8px 16px', background: colors.primary, border: 'none',
            borderRadius: 6, color: colors.white, fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}>
            Copiar diagrama
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────
// Modal novo chamado (com Estruturador IA + anexos)
// ─────────────────────────────────────
function ModalNovoChamado({ onClose, onCriado }) {
  const [textoLivre, setTextoLivre] = useState('');
  const [estruturando, setEstruturando] = useState(false);
  const [erroIA, setErroIA] = useState(null);

  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [categoria, setCategoria] = useState('melhoria');
  const [prioridade, setPrioridade] = useState('media');
  const [arquivos, setArquivos] = useState([]); // File[]
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState(null);
  const fileInputRef = useRef(null);

  const MAX_FILES = 10;
  const MAX_SIZE = 10 * 1024 * 1024;

  const podeEnviar = titulo.trim().length >= 3 && descricao.trim().length >= 5 && !saving;

  const estruturarIA = async () => {
    if (!textoLivre.trim() || textoLivre.trim().length < 5) return;
    setEstruturando(true);
    setErroIA(null);
    try {
      const res = await fetch(`${API}/chamados/estruturar-ia`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ texto: textoLivre.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setTitulo(data.titulo || '');
      setCategoria(data.categoria || 'melhoria');
      setPrioridade(data.prioridade || 'media');
      setDescricao(data.descricao || '');
    } catch (e) {
      setErroIA(e.message);
    } finally {
      setEstruturando(false);
    }
  };

  const onFilesSelected = (files) => {
    const novos = Array.from(files);
    const validos = [];
    const erros = [];
    for (const f of novos) {
      if (f.size > MAX_SIZE) {
        erros.push(`${f.name} excede 10MB`);
        continue;
      }
      validos.push(f);
    }
    const total = arquivos.length + validos.length;
    let final = [...arquivos, ...validos];
    if (total > MAX_FILES) {
      erros.push(`Máximo de ${MAX_FILES} arquivos. Alguns foram descartados.`);
      final = final.slice(0, MAX_FILES);
    }
    if (erros.length) setErro(erros.join(' · '));
    setArquivos(final);
  };

  const removerArquivo = (idx) => {
    setArquivos(arquivos.filter((_, i) => i !== idx));
  };

  const fmtSize = (b) => {
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
  };

  const handleSubmit = async () => {
    setSaving(true);
    setErro(null);
    try {
      // 1) Cria o chamado
      const res = await fetch(`${API}/chamados`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          titulo: titulo.trim(), descricao: descricao.trim(), categoria, prioridade,
        }),
      });
      const chamado = await res.json();
      if (!res.ok) throw new Error(chamado.error || `HTTP ${res.status}`);

      // 2) Upload de cada anexo em sequência
      for (const f of arquivos) {
        const fd = new FormData();
        fd.append('arquivo', f);
        const token = localStorage.getItem('xm_token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const upRes = await fetch(`${API}/chamados/${chamado.id}/anexos`, {
          method: 'POST',
          headers,
          body: fd,
        });
        if (!upRes.ok) {
          const d = await upRes.json().catch(() => ({}));
          console.warn('Falha ao anexar', f.name, d.error);
        }
      }

      onCriado(chamado);
    } catch (e) {
      setErro(e.message);
      setSaving(false);
    }
  };

  const labelStyle = {
    display: 'block', fontSize: 11, fontWeight: 700, color: colors.textMuted,
    letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 6,
  };
  const inputStyle = {
    width: '100%', padding: '10px 12px', background: colors.bg,
    border: `1px solid ${colors.border}`, borderRadius: 8, color: colors.text,
    fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit',
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20,
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: colors.card, borderRadius: 16, width: '100%', maxWidth: 640,
        maxHeight: '92vh', overflow: 'auto', border: `1px solid ${colors.border}`,
      }}>
        {/* Header */}
        <div style={{
          padding: '18px 22px', borderBottom: `1px solid ${colors.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          position: 'sticky', top: 0, background: colors.card, zIndex: 1,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <LifeBuoy size={22} color={colors.primary} />
            <div>
              <div style={{ fontWeight: 700, fontSize: 18, color: colors.text }}>Novo chamado</div>
              <div style={{ fontSize: 12, color: colors.textMuted }}>
                Descreva o ajuste/correção que você precisa no sistema.
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer', padding: 4 }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 18 }}>
          {/* Estruturador IA */}
          <div style={{
            padding: 16, background: `${colors.primary}12`,
            border: `1px solid ${colors.primary}44`, borderRadius: 12,
            display: 'flex', flexDirection: 'column', gap: 10,
          }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: colors.primary, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Wand2 size={15} /> Prefere descrever em linguagem natural?
              </div>
              <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 4 }}>
                Digite do jeito que vier à cabeça e a IA estrutura pra você (título, categoria, descrição técnica).
              </div>
            </div>
            <textarea
              value={textoLivre}
              onChange={e => setTextoLivre(e.target.value)}
              rows={3}
              placeholder="Ex: tem um bug na tela de vendas, quando clico no filtro de data só do último mês ele não filtra direito, mostra vendas antigas..."
              style={{ ...inputStyle, resize: 'vertical' }}
            />
            {erroIA && (
              <div style={{ fontSize: 12, color: colors.danger }}>{erroIA}</div>
            )}
            <button
              onClick={estruturarIA}
              disabled={estruturando || textoLivre.trim().length < 5}
              style={{
                width: '100%', padding: '10px 14px',
                background: estruturando || textoLivre.trim().length < 5 ? colors.textDim : '#1e293b',
                border: `1px solid ${colors.borderLight}`, borderRadius: 8,
                color: colors.white, fontSize: 13, fontWeight: 700,
                cursor: estruturando || textoLivre.trim().length < 5 ? 'not-allowed' : 'pointer',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              <Wand2 size={14} /> <Sparkles size={13} /> {estruturando ? 'Estruturando...' : 'Estruturar com IA'}
            </button>
          </div>

          {/* Título */}
          <div>
            <label style={labelStyle}>Título</label>
            <input
              value={titulo}
              onChange={e => setTitulo(e.target.value)}
              maxLength={200}
              placeholder="Ex: Filtro de data em Vendas ignorando mês selecionado"
              style={inputStyle}
            />
            <div style={{ fontSize: 11, color: colors.textDim, marginTop: 4, textAlign: 'right' }}>
              {titulo.length}/200
            </div>
          </div>

          {/* Categoria / Prioridade */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Categoria</label>
              <select
                value={categoria}
                onChange={e => setCategoria(e.target.value)}
                style={inputStyle}
              >
                <option value="melhoria">💡 Melhoria</option>
                <option value="bug">🐛 Bug</option>
                <option value="nova_feature">✨ Nova feature</option>
                <option value="duvida">❓ Dúvida</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Prioridade</label>
              <select
                value={prioridade}
                onChange={e => setPrioridade(e.target.value)}
                style={inputStyle}
              >
                <option value="baixa">Baixa</option>
                <option value="media">Média</option>
                <option value="alta">Alta</option>
                <option value="critica">Crítica</option>
              </select>
            </div>
          </div>

          {/* Descrição */}
          <div>
            <label style={labelStyle}>Descrição</label>
            <textarea
              value={descricao}
              onChange={e => setDescricao(e.target.value)}
              maxLength={10000}
              rows={7}
              placeholder="Descreva: o que você esperava, o que aconteceu, como reproduzir, qual tela, etc. Markdown permitido."
              style={{ ...inputStyle, resize: 'vertical' }}
            />
            <div style={{ fontSize: 11, color: colors.textDim, marginTop: 4, textAlign: 'right' }}>
              {descricao.length}/10.000
            </div>
          </div>

          {/* Anexos */}
          <div>
            <label style={labelStyle}>
              <Paperclip size={11} style={{ display: 'inline', verticalAlign: 'text-bottom' }} /> Anexos
            </label>
            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={e => { e.preventDefault(); }}
              onDrop={e => {
                e.preventDefault();
                if (e.dataTransfer.files?.length) onFilesSelected(e.dataTransfer.files);
              }}
              style={{
                border: `2px dashed ${colors.border}`, borderRadius: 10,
                padding: '18px 16px', textAlign: 'center', cursor: 'pointer',
                background: colors.bg, color: colors.textMuted, fontSize: 13,
                transition: 'border-color 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = colors.primary}
              onMouseLeave={e => e.currentTarget.style.borderColor = colors.border}
            >
              <Paperclip size={16} style={{ display: 'inline', verticalAlign: 'text-bottom', marginRight: 6 }} />
              Clique pra adicionar print, PDF, planilha ou documento
              <div style={{ fontSize: 11, color: colors.textDim, marginTop: 4 }}>
                10MB por arquivo, até {MAX_FILES} arquivos
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={e => {
                if (e.target.files?.length) onFilesSelected(e.target.files);
                e.target.value = '';
              }}
            />
            {arquivos.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
                {arquivos.map((f, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '6px 10px', background: colors.bg,
                    border: `1px solid ${colors.border}`, borderRadius: 6,
                    fontSize: 12,
                  }}>
                    <Paperclip size={12} color={colors.textMuted} />
                    <span style={{ color: colors.text, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {f.name}
                    </span>
                    <span style={{ color: colors.textDim, fontSize: 11 }}>{fmtSize(f.size)}</span>
                    <button
                      onClick={() => removerArquivo(i)}
                      style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer', padding: 2 }}
                    >
                      <X size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {erro && (
            <div style={{
              padding: '10px 14px', background: `${colors.danger}15`,
              border: `1px solid ${colors.danger}40`, borderRadius: 8,
              fontSize: 13, color: colors.danger,
            }}>
              {erro}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '14px 22px', borderTop: `1px solid ${colors.border}`,
          display: 'flex', justifyContent: 'flex-end', gap: 10,
          position: 'sticky', bottom: 0, background: colors.card,
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px', background: 'transparent',
              border: `1px solid ${colors.border}`, borderRadius: 8,
              color: colors.text, fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={!podeEnviar}
            style={{
              padding: '10px 22px', background: podeEnviar ? colors.primaryDark : colors.textDim,
              border: 'none', borderRadius: 8, color: colors.white,
              fontSize: 13, fontWeight: 700, cursor: podeEnviar ? 'pointer' : 'not-allowed',
            }}
          >
            {saving ? 'Enviando...' : 'Enviar chamado'}
          </button>
        </div>
      </div>
    </div>
  );
}
